/**
 * Serviço de API REST para comunicação com o backend
 * Substitui as chamadas do Firebase
 */

/**
 * Base URL do backend.
 *
 * Em produção NÃO use "localhost" aqui — no browser, "localhost" é a máquina do usuário.
 * Configure `REACT_APP_API_URL` (ex.: "https://seu-dominio.com/api") ou use um proxy reverso e
 * defina como "/api".
 */
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Obter token de autenticação do localStorage ou sessionStorage
 */
function getAuthToken() {
  // Primeiro tenta localStorage (permanecer conectado)
  let token = localStorage.getItem('authToken');
  if (!token) {
    // Se não encontrar, tenta sessionStorage (sessão temporária)
    token = sessionStorage.getItem('authToken');
  }
  return token;
}

/**
 * Obter empresa selecionada do localStorage
 */
function getSelectedCompany() {
  try {
    const stored = localStorage.getItem('selectedCompany');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Erro ao carregar empresa selecionada:', error);
  }
  return null;
}

/**
 * Adicionar empresa_id aos filtros ou dados se houver empresa selecionada
 */
function addEmpresaIdToData(data) {
  const selectedCompany = getSelectedCompany();
  if (selectedCompany && selectedCompany.id) {
    if (Array.isArray(data)) {
      // Se for array, adicionar empresa_id a cada item
      return data.map(item => ({
        ...item,
        empresa_id: selectedCompany.id
      }));
    } else if (typeof data === 'object' && data !== null) {
      // Se for objeto, adicionar empresa_id
      const result = {
        ...data,
        empresa_id: selectedCompany.id
      };
      return result;
    }
  } else {
    // Silenciar logs de debug: ausência de empresa selecionada pode ser válida em alguns fluxos.
  }
  return data;
}

/**
 * Adicionar empresa_id aos query params se houver empresa selecionada
 */
function addEmpresaIdToQueryParams(queryParams) {
  const selectedCompany = getSelectedCompany();
  if (selectedCompany && selectedCompany.id) {
    queryParams.append('empresa_id', selectedCompany.id);
  }
  return queryParams;
}

/**
 * Salvar token de autenticação no localStorage ou sessionStorage
 * @param {string} token - Token de autenticação
 * @param {boolean} rememberMe - Se true, salva no localStorage (persistente), senão no sessionStorage (temporário)
 */
function setAuthToken(token, rememberMe = false) {
  if (token) {
    if (rememberMe) {
      // Salvar no localStorage (persiste após fechar o navegador)
      localStorage.setItem('authToken', token);
      // Remover do sessionStorage se existir
      sessionStorage.removeItem('authToken');
    } else {
      // Salvar no sessionStorage (apenas durante a sessão)
      sessionStorage.setItem('authToken', token);
      // Remover do localStorage se existir
      localStorage.removeItem('authToken');
    }
  } else {
    // Limpar ambos ao fazer logout
    localStorage.removeItem('authToken');
    sessionStorage.removeItem('authToken');
  }
}

/**
 * Função auxiliar para fazer requisições HTTP
 */
async function request(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;
  const token = getAuthToken();
  
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers,
    },
    ...options,
  };

  // Se for blob, não definir Content-Type como JSON
  if (options.responseType === 'blob') {
    delete config.headers['Content-Type'];
  }

  if (config.body && typeof config.body === 'object') {
    config.body = JSON.stringify(config.body);
  }

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      let errorData;
      let errorText = '';
      try {
        errorText = await response.text();
        errorData = errorText ? JSON.parse(errorText) : { 
          erro: 'Erro na requisição',
          detalhes: response.statusText 
        };
      } catch (e) {
        // Se não conseguir fazer parse, usar o texto bruto
        errorData = { 
          erro: 'Erro na requisição',
          detalhes: errorText || response.statusText,
          status: response.status,
          rawError: errorText
        };
      }
      console.error('Erro na resposta da API:', {
        status: response.status,
        statusText: response.statusText,
        url: url,
        errorData: errorData,
        errorText: errorText
      });
      
      // Função auxiliar para extrair mensagem de erro de forma segura
      const extrairMensagemErro = (data) => {
        if (!data) return 'Erro desconhecido';
        
        // Tentar diferentes campos comuns
        if (typeof data === 'string') {
          // Se for string JSON, tentar parsear
          try {
            const parsed = JSON.parse(data);
            return extrairMensagemErro(parsed);
          } catch (e) {
            return data;
          }
        }
        
        // Verificar mensagem direta
        if (data.mensagem) return String(data.mensagem);
        if (data.message) return String(data.message);
        
        // Verificar erro
        if (data.erro) {
          // Se erro for string, retornar
          if (typeof data.erro === 'string') return data.erro;
          // Se erro for objeto, extrair mensagem dele
          if (typeof data.erro === 'object' && data.erro.message) {
            return String(data.erro.message);
          }
        }
        
        // Verificar detalhes
        if (data.detalhes) {
          if (typeof data.detalhes === 'string') return data.detalhes;
          if (typeof data.detalhes === 'object' && data.detalhes.mensagem) {
            return String(data.detalhes.mensagem);
          }
        }
        
        // Verificar error (formato em inglês)
        if (data.error) {
          if (typeof data.error === 'string') return data.error;
          if (typeof data.error === 'object') {
            if (data.error.message) return String(data.error.message);
            if (data.error.detalhes) return String(data.error.detalhes);
            if (data.error.mensagem) return String(data.error.mensagem);
          }
        }
        
        // Se for um array de erros, formatar
        if (Array.isArray(data.erros) && data.erros.length > 0) {
          const mensagens = data.erros.map(err => {
            if (typeof err === 'string') return err;
            if (err.mensagem) return err.mensagem;
            if (err.message) return err.message;
            if (err.campo && err.mensagem) return `${err.campo}: ${err.mensagem}`;
            return JSON.stringify(err);
          });
          return mensagens.join('; ');
        }
        
        // Verificar se há erros de validação aninhados
        if (data.error?.errors && Array.isArray(data.error.errors)) {
          const mensagens = data.error.errors.map(err => {
            if (typeof err === 'string') return err;
            if (err.mensagem) return err.mensagem;
            if (err.message) return err.message;
            if (err.campo && err.mensagem) return `${err.campo}: ${err.mensagem}`;
            return JSON.stringify(err);
          });
          return mensagens.join('; ');
        }
        
        // Último recurso: tentar stringify
        try {
          const str = JSON.stringify(data);
          if (str.length < 500) return str;
          return 'Erro interno do servidor (resposta muito longa)';
        } catch (e) {
          return 'Erro interno do servidor';
        }
      };
      
      // Mensagens mais específicas para diferentes status
      if (response.status === 401) {
        // Token inválido ou ausente
        const currentToken = getAuthToken();
        const currentPath = window.location.pathname;
        
        // Rotas públicas que não devem redirecionar mesmo com erro 401
        const publicRoutes = ['/signin', '/', '/forgot', '/iniciar-viagem', '/temp', '/rate'];
        const isPublicRoute = publicRoutes.some(route => currentPath.startsWith(route));
        
        if (!currentToken) {
          // Não há token - usuário precisa fazer login
          // Redirecionar para login apenas se não estiver em rota pública
          if (!isPublicRoute) {
            setTimeout(() => {
              if (currentPath !== '/signin' && currentPath !== '/') {
                window.location.href = '/signin';
              }
            }, 1000);
          }
          throw new Error('Token de autenticação não fornecido. Por favor, faça login novamente.');
        } else {
          // Token existe mas foi rejeitado - pode ter expirado ou ser inválido
          setAuthToken(null);
          // Redirecionar para login apenas se não estiver em rota pública
          if (!isPublicRoute) {
            setTimeout(() => {
              if (currentPath !== '/signin' && currentPath !== '/') {
                window.location.href = '/signin';
              }
            }, 1000);
          }
          throw new Error('Sessão expirada. Por favor, faça login novamente.');
        }
      } else if (response.status === 404) {
        throw new Error(`Rota não encontrada: ${url}. Verifique se o backend está rodando e se a rota está correta.`);
      } else if (response.status === 503 || errorData.tipo === 'servico_indisponivel') {
        throw new Error('Servidor do banco de dados está temporariamente indisponível. Por favor, tente novamente em alguns minutos.');
      } else if (response.status === 500) {
        const mensagem = extrairMensagemErro(errorData);
        console.error('Erro 500 na API:', mensagem);
        throw new Error(mensagem || 'Erro interno do servidor');
      } else {
        const mensagem = extrairMensagemErro(errorData);
        throw new Error(mensagem || `Erro na requisição (${response.status})`);
      }
    }

    // Verificar se é um arquivo binário (blob)
    if (options.responseType === 'blob') {
      const blob = await response.blob();
      return blob;
    }

    const text = await response.text();
    // Tratar caso especial quando a resposta é "null" (string)
    if (text === 'null' || text.trim() === 'null') {
      return null;
    }
    
    const data = text ? JSON.parse(text) : null;
    return data;
  } catch (error) {
    console.error('Erro na requisição API:', error);
    
    // Verificar diferentes tipos de erros de conexão
    const errorMessage = error.message || error.toString();
    
    if (
      errorMessage.includes('Failed to fetch') || 
      errorMessage.includes('NetworkError') ||
      errorMessage.includes('fetch failed') ||
      errorMessage.includes('ERR_CONNECTION_REFUSED') ||
      errorMessage.includes('ECONNREFUSED')
    ) {
      throw new Error(
        `Não foi possível conectar ao servidor em ${API_BASE_URL}. ` +
        `Verifique se o backend está rodando na porta 3005. ` +
        `Erro: ${errorMessage}`
      );
    }
    
    throw error;
  }
}

/**
 * API de Viagens
 */
export const viagensAPI = {
  /**
   * Listar todas as viagens
   */
  listar: async (filtros = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(filtros).forEach(key => {
      if (filtros[key] !== undefined && filtros[key] !== null) {
        // Se for array (como carga_ids), converter para JSON string
        if (Array.isArray(filtros[key])) {
          queryParams.append(key, JSON.stringify(filtros[key]));
        } else {
          queryParams.append(key, filtros[key]);
        }
      }
    });
    
    const queryString = queryParams.toString();
    const endpoint = `/viagens${queryString ? `?${queryString}` : ''}`;
    
    const response = await request(endpoint, { method: 'GET' });
    return response.dados || [];
  },

  /**
   * Buscar viagem por ID
   */
  buscarPorId: async (id) => {
    const response = await request(`/viagens/${id}`, { method: 'GET' });
    return response.dados;
  },

  /**
   * Criar nova viagem
   */
  criar: async (dadosViagem) => {
    // Adicionar empresa_id automaticamente se houver empresa selecionada
    const dadosComEmpresa = addEmpresaIdToData(dadosViagem);
    
    const response = await request('/viagens', {
      method: 'POST',
      body: dadosComEmpresa,
    });
    return response.dados;
  },

  /**
   * Atualizar viagem
   */
  atualizar: async (id, dadosViagem) => {
    // Adicionar empresa_id automaticamente se houver empresa selecionada
    const dadosComEmpresa = addEmpresaIdToData(dadosViagem);
    
    const response = await request(`/viagens/${id}`, {
      method: 'PUT',
      body: dadosComEmpresa,
    });
    return response.dados;
  },

  /**
   * Deletar viagem
   */
  deletar: async (id) => {
    await request(`/viagens/${id}`, { method: 'DELETE' });
    return { sucesso: true };
  },

  /**
   * Buscar invoices de uma viagem
   */
  buscarInvoices: async (viagemId) => {
    const response = await request(`/viagens/${viagemId}/invoices`, { method: 'GET' });
    return response.dados || [];
  },

  /**
   * Adicionar invoice a uma viagem
   */
  adicionarInvoice: async (viagemId, dadosInvoice) => {
    const response = await request(`/viagens/${viagemId}/invoices`, {
      method: 'POST',
      body: dadosInvoice,
    });
    return response.dados;
  },

  /**
   * Remover invoice de uma viagem
   */
  removerInvoice: async (viagemId, invoiceId) => {
    await request(`/viagens/${viagemId}/invoices/${invoiceId}`, { method: 'DELETE' });
    return { sucesso: true };
  },
};

/**
 * API de Fretes (módulo Embarcadores)
 */
export const fretesAPI = {
  listar: async (filtros = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(filtros).forEach((key) => {
      if (filtros[key] !== undefined && filtros[key] !== null && filtros[key] !== '') {
        queryParams.append(key, filtros[key]);
      }
    });
    const queryString = queryParams.toString();
    const endpoint = `/fretes${queryString ? `?${queryString}` : ''}`;
    const response = await request(endpoint, { method: 'GET' });
    return response.dados || [];
  },

  buscarPorId: async (id) => {
    const response = await request(`/fretes/${id}`, { method: 'GET' });
    return response.dados;
  },

  criar: async (dados) => {
    const dadosComEmpresa = addEmpresaIdToData(dados);
    const response = await request('/fretes', {
      method: 'POST',
      body: dadosComEmpresa,
    });
    return response.dados;
  },

  atualizar: async (id, dados) => {
    const dadosComEmpresa = addEmpresaIdToData(dados);
    const response = await request(`/fretes/${id}`, {
      method: 'PUT',
      body: dadosComEmpresa,
    });
    return response.dados;
  },

  deletar: async (id) => {
    await request(`/fretes/${id}`, { method: 'DELETE' });
    return { sucesso: true };
  },

  publicar: async (id) => {
    const response = await request(`/fretes/${id}/publicar`, {
      method: 'POST',
    });
    return response.dados;
  },

  cancelar: async (id, motivoCancelamento) => {
    const response = await request(`/fretes/${id}/cancelar`, {
      method: 'POST',
      body: { motivo_cancelamento: motivoCancelamento },
    });
    return response.dados;
  },

  aprovar: async (id) => {
    const response = await request(`/fretes/${id}/aprovar`, {
      method: 'POST',
    });
    return response.dados;
  },

  reprovar: async (id, justificativa) => {
    const response = await request(`/fretes/${id}/reprovar`, {
      method: 'POST',
      body: { justificativa },
    });
    return response.dados;
  },

  // XMLs do frete
  listarXmls: async (freteId) => {
    const response = await request(`/fretes/${freteId}/xml`, {
      method: 'GET',
    });
    return response.dados;
  },

  anexarXml: async (freteId, xmlData) => {
    const response = await request(`/fretes/${freteId}/xml`, {
      method: 'POST',
      body: xmlData,
    });
    return response.dados;
  },

  removerXml: async (freteId, xmlId) => {
    const response = await request(`/fretes/${freteId}/xml/${xmlId}`, {
      method: 'DELETE',
    });
    return response.dados;
  },

  gerarViagens: async (id) => {
    const response = await request(`/fretes/${id}/gerar-viagens`, {
      method: 'POST',
    });
    return response.dados;
  },
};

/**
 * API de CT-e
 */
export const cteAPI = {
  /**
   * Listar CT-e
   */
  listar: async (filtros = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(filtros).forEach(key => {
      if (filtros[key] !== undefined && filtros[key] !== null) {
        queryParams.append(key, filtros[key]);
      }
    });
    
    const queryString = queryParams.toString();
    const endpoint = `/cte${queryString ? `?${queryString}` : ''}`;
    
    const response = await request(endpoint, { method: 'GET' });
    return response.dados || [];
  },

  /**
   * Buscar eventos de CT-e por viagem
   */
  buscarEventosPorViagem: async (viagemId) => {
    const response = await request(`/cte/eventos/viagem/${viagemId}`, { method: 'GET' });
    return response || [];
  },

  /**
   * Buscar último evento de uma viagem
   */
  buscarUltimoEvento: async (viagemId) => {
    const response = await request(`/cte/eventos/viagem/${viagemId}/ultimo`, { method: 'GET' });
    return response;
  },

  /**
   * Listar eventos com filtros
   */
  listarEventos: async (filtros = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(filtros).forEach(key => {
      if (filtros[key] !== undefined && filtros[key] !== null) {
        queryParams.append(key, filtros[key]);
      }
    });
    
    const queryString = queryParams.toString();
    const endpoint = `/cte/eventos${queryString ? `?${queryString}` : ''}`;
    
    const response = await request(endpoint, { method: 'GET' });
    return response || [];
  },

  /**
   * Processar XML e gerar CT-e
   */
  processarXml: async (xml) => {
    const response = await request('/cte/processar-xml', {
      method: 'POST',
      body: { xml },
    });
    return response.dados;
  },

  /**
   * Buscar CT-e por ID
   */
  buscarPorId: async (id) => {
    const response = await request(`/cte/${id}`, { method: 'GET' });
    return response.dados;
  },

  /**
   * Consultar CT-e na API Nuvem Fiscal
   * GET /cte/:id - Consulta o CT-e usando o ID único do evento gerado pela API
   */
  consultarCte: async (cteId) => {
    const response = await request(`/cte/${cteId}`, { method: 'GET' });
    return response;
  },

  /**
   * Emitir CT-e
   */
  emitir: async (dadosCte) => {
    const response = await request('/cte', {
      method: 'POST',
      body: dadosCte,
    });
    return response;
  },

  /**
   * Cancelar CT-e
   */
  cancelar: async (id, justificativa) => {
    const response = await request(`/cte/${id}/cancelar`, {
      method: 'POST',
      body: { justificativa },
    });
    return response;
  },

  /**
   * Sincronizar CT-e com a SEFAZ
   * Atualiza o status do CT-e consultando a API Nuvem Fiscal
   */
  sincronizarCte: async (cteId) => {
    const response = await request(`/cte/${cteId}/sincronizar`, {
      method: 'POST',
    });
    return response;
  },

  /**
   * Consultar status da SEFAZ
   * GET /cte/status-sefaz
   * Não requer parâmetros - apenas verifica se o serviço da SEFAZ está funcionando
   */
  consultarStatusSefaz: async () => {
    const response = await request('/cte/status-sefaz', { method: 'GET' });
    return response;
  },

  /**
   * Consultar CEP
   * GET /cte/cep/:cep
   * Consulta CEP na API Nuvem Fiscal e retorna dados do endereço
   */
  consultarCep: async (cep) => {
    const cepLimpo = cep.replace(/\D/g, '');
    const response = await request(`/cte/cep/${cepLimpo}`, { method: 'GET' });
    return response;
  },

  /**
   * Baixar PDF do CT-e
   * GET /cte/:id/pdf
   */
  baixarPdf: async (cteId) => {
    const blob = await request(`/cte/${cteId}/pdf`, { 
      method: 'GET',
      responseType: 'blob'
    });
    return blob;
  },

  /**
   * Baixar XML do conhecimento
   * GET /cte/:id/xml-conhecimento
   */
  baixarXmlConhecimento: async (cteId) => {
    const blob = await request(`/cte/${cteId}/xml-conhecimento`, { 
      method: 'GET',
      responseType: 'blob'
    });
    return blob;
  },
};

/**
 * API de MDF-e
 */
export const mdfeAPI = {
  /**
   * Listar MDF-e
   */
  listar: async (filtros = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(filtros).forEach(key => {
      if (filtros[key] !== undefined && filtros[key] !== null) {
        queryParams.append(key, filtros[key]);
      }
    });
    
    const queryString = queryParams.toString();
    const endpoint = `/mdfe${queryString ? `?${queryString}` : ''}`;
    
    const response = await request(endpoint, { method: 'GET' });
    return response.dados || [];
  },

  /**
   * Buscar MDF-e por ID
   */
  buscarPorId: async (id) => {
    const response = await request(`/mdfe/${id}`, { method: 'GET' });
    return response.dados;
  },

  /**
   * Emitir MDF-e
   */
  emitir: async (dadosMdfe) => {
    const response = await request('/mdfe', {
      method: 'POST',
      body: dadosMdfe,
    });
    return response;
  },

  /**
   * Cancelar MDF-e
   */
  cancelar: async (id, justificativa) => {
    const response = await request(`/mdfe/${id}/cancelar`, {
      method: 'POST',
      body: { justificativa },
    });
    return response;
  },

  /**
   * Consultar MDF-e por ID
   */
  consultar: async (mdfeId) => {
    const response = await request(`/mdfe/${mdfeId}`, { method: 'GET' });
    return response;
  },


  /**
   * Encerrar MDF-e
   */
  encerrar: async (id, dadosEncerramento) => {
    const response = await request(`/mdfe/${id}/encerrar`, {
      method: 'POST',
      body: dadosEncerramento,
    });
    return response;
  },

  /**
   * Sincronizar MDF-e com a SEFAZ
   */
  sincronizar: async (mdfeId) => {
    const response = await request(`/mdfe/${mdfeId}/sincronizar`, {
      method: 'POST',
    });
    return response;
  },

  /**
   * Baixar PDF do MDF-e
   */
  baixarPdf: async (mdfeId) => {
    const blob = await request(`/mdfe/${mdfeId}/pdf`, { 
      method: 'GET',
      responseType: 'blob'
    });
    return blob;
  },

  /**
   * Baixar XML do MDF-e
   */
  baixarXml: async (mdfeId) => {
    const blob = await request(`/mdfe/${mdfeId}/xml`, { 
      method: 'GET',
      responseType: 'blob'
    });
    return blob;
  },

  /**
   * Baixar XML do Manifesto do MDF-e
   */
  baixarXmlManifesto: async (mdfeId) => {
    const blob = await request(`/mdfe/${mdfeId}/xml/manifesto`, { 
      method: 'GET',
      responseType: 'blob'
    });
    return blob;
  },

  /**
   * Buscar último evento de uma carga
   * Retorna null se não houver evento (ao invés de lançar erro)
   */
  buscarUltimoEvento: async (cargaId) => {
    try {
      const response = await request(`/mdfe/eventos/carga/${cargaId}/ultimo`, { method: 'GET' });
      // Se a resposta for null ou undefined, significa que não existe evento ainda
      return response || null;
    } catch (error) {
      // Se for erro 404, verificar se é erro de rota ou evento não encontrado
      if (error.message && error.message.includes('Rota não encontrada')) {
        // Se a URL contém /eventos/carga/, provavelmente é evento não encontrado
        // Mas agora o backend retorna 200 com null, então não deveria chegar aqui
        if (error.message.includes('/eventos/carga/')) {
          console.log('⚠️ [mdfeAPI] Evento não encontrado para carga:', cargaId);
          return null;
        }
        // Se não for evento, é realmente erro de rota
        throw error;
      }
      // Para outros erros, relançar
      throw error;
    }
  },

  /**
   * Buscar eventos por carga
   */
  buscarEventosPorCarga: async (cargaId) => {
    const response = await request(`/mdfe/eventos/carga/${cargaId}`, { method: 'GET' });
    return response || [];
  },
};

/**
 * API de Autenticação
 */
export const authAPI = {
  /**
   * Login
   * @param {string} email - Email do usuário
   * @param {string} password - Senha do usuário
   * @param {boolean} rememberMe - Se true, salva token no localStorage (persistente), senão no sessionStorage
   */
  login: async (email, password, rememberMe = false) => {
    console.log('🔐 Tentando fazer login com:', email, 'rememberMe:', rememberMe);
    try {
      // Fazer requisição sem token (login não precisa de autenticação)
      const url = `${API_BASE_URL}/auth/login`;
      const config = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      };

      console.log('🌐 Fazendo requisição de login:', { url, method: 'POST' });

      const response = await fetch(url, config);
      
      if (!response.ok) {
        let errorData;
        try {
          const text = await response.text();
          errorData = text ? JSON.parse(text) : { erro: 'Erro na requisição' };
        } catch (e) {
          errorData = { erro: 'Erro na requisição', detalhes: response.statusText };
        }
        throw new Error(errorData.detalhes || errorData.erro || `Erro no login (${response.status})`);
      }

      const responseData = await response.json();
      console.log('📦 Resposta completa do login:', responseData);
      
      // Verificar diferentes estruturas possíveis da resposta
      let token = null;
      let usuario = null;

      if (responseData.sucesso && responseData.dados) {
        // Estrutura esperada: { sucesso: true, dados: { usuario: {...}, token: "..." } }
        if (responseData.dados.token) {
          token = responseData.dados.token;
        }
        if (responseData.dados.usuario) {
          usuario = responseData.dados.usuario;
        }
      } else if (responseData.token) {
        // Estrutura alternativa: { token: "...", usuario: {...} }
        token = responseData.token;
        usuario = responseData.usuario;
      } else if (responseData.dados && responseData.dados.token) {
        // Outra estrutura possível
        token = responseData.dados.token;
        usuario = responseData.dados.usuario || responseData.dados;
      }

      console.log('🔍 Token extraído:', token ? 'Sim' : 'Não');
      console.log('🔍 Usuário extraído:', usuario ? 'Sim' : 'Não');

      if (token) {
        setAuthToken(token, rememberMe);
        const storageType = rememberMe ? 'localStorage' : 'sessionStorage';
        console.log(`✅ Token salvo no ${storageType} com sucesso`);
        console.log('🔑 Token (primeiros 20 chars):', token.substring(0, 20) + '...');
        
        // Verificar se foi salvo corretamente
        const tokenVerificado = getAuthToken();
        if (tokenVerificado === token) {
          console.log(`✅ Token verificado no ${storageType}: OK`);
        } else {
          console.error(`❌ Token não foi salvo corretamente no ${storageType}`);
        }
      } else {
        console.error('❌ Token não encontrado na resposta:', responseData);
        throw new Error('Token não recebido do servidor');
      }

      if (!usuario) {
        console.warn('⚠️ Usuário não encontrado na resposta, mas token foi salvo');
      }
      
      return { usuario, token };
    } catch (error) {
      console.error('❌ Erro no login:', error);
      throw error;
    }
  },

  /**
   * Logout
   */
  logout: () => {
    setAuthToken(null);
  },

  /**
   * Verificar token
   */
  verify: async () => {
    const response = await request('/auth/verify', { method: 'GET' });
    return response.dados;
  },

  /**
   * Buscar usuário atual
   */
  me: async () => {
    const response = await request('/auth/me', { method: 'GET' });
    return response.dados;
  },

  /**
   * Atualizar usuário atual
   */
  updateMe: async (dados) => {
    const response = await request('/auth/me', {
      method: 'PUT',
      body: dados,
    });
    return response.dados;
  },

  /**
   * Recuperar senha
   */
  forgotPassword: async (email) => {
    const response = await request('/auth/forgot-password', {
      method: 'POST',
      body: { email },
    });
    return response;
  },
};

/**
 * API de Providers
 */
export const providersAPI = {
  listar: async () => {
    const response = await request('/providers', { method: 'GET' });
    return response.dados || [];
  },

  buscarPorId: async (id) => {
    const response = await request(`/providers/${id}`, { method: 'GET' });
    return response.dados;
  },

  criar: async (dados) => {
    const response = await request('/providers', {
      method: 'POST',
      body: dados,
    });
    return response.dados;
  },

  atualizar: async (id, dados) => {
    const response = await request(`/providers/${id}`, {
      method: 'PUT',
      body: dados,
    });
    return response.dados;
  },

  deletar: async (id) => {
    await request(`/providers/${id}`, { method: 'DELETE' });
    return { sucesso: true };
  },
};

/**
 * API de Truckers
 */
export const truckersAPI = {
  listar: async (filtros = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(filtros).forEach(key => {
      if (filtros[key] !== undefined && filtros[key] !== null) {
        queryParams.append(key, filtros[key]);
      }
    });
    
    // Adicionar empresa_id automaticamente se houver empresa selecionada
    addEmpresaIdToQueryParams(queryParams);
    
    const queryString = queryParams.toString();
    const endpoint = `/truckers${queryString ? `?${queryString}` : ''}`;
    
    const response = await request(endpoint, { method: 'GET' });
    return response.dados || [];
  },

  buscarPorId: async (id) => {
    const response = await request(`/truckers/${id}`, { method: 'GET' });
    return response.dados;
  },

  buscarPorCpf: async (cpf) => {
    const response = await request(`/truckers/cpf/${cpf}`, { method: 'GET' });
    return response.dados;
  },

  criar: async (dados) => {
    // Adicionar empresa_id automaticamente se houver empresa selecionada
    const dadosComEmpresa = addEmpresaIdToData(dados);
    
    const response = await request('/truckers', {
      method: 'POST',
      body: dadosComEmpresa,
    });
    return response.dados;
  },

  atualizar: async (id, dados) => {
    // Adicionar empresa_id automaticamente se houver empresa selecionada
    const dadosComEmpresa = addEmpresaIdToData(dados);
    
    const response = await request(`/truckers/${id}`, {
      method: 'PUT',
      body: dadosComEmpresa,
    });
    return response.dados;
  },

  deletar: async (id) => {
    await request(`/truckers/${id}`, { method: 'DELETE' });
    return { sucesso: true };
  },
};

/**
 * API de Insurances
 */
export const insurancesAPI = {
  listar: async (filtros = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(filtros).forEach(key => {
      if (filtros[key] !== undefined && filtros[key] !== null) {
        queryParams.append(key, filtros[key]);
      }
    });
    
    // Adicionar empresa_id automaticamente se houver empresa selecionada
    addEmpresaIdToQueryParams(queryParams);
    
    const queryString = queryParams.toString();
    const endpoint = `/insurances${queryString ? `?${queryString}` : ''}`;
    
    const response = await request(endpoint, { method: 'GET' });
    return response.dados || [];
  },

  buscarPorId: async (id) => {
    const response = await request(`/insurances/${id}`, { method: 'GET' });
    return response.dados;
  },

  criar: async (dados) => {
    // Adicionar empresa_id automaticamente se houver empresa selecionada
    const dadosComEmpresa = addEmpresaIdToData(dados);
    
    const response = await request('/insurances', {
      method: 'POST',
      body: dadosComEmpresa,
    });
    return response.dados;
  },

  atualizar: async (id, dados) => {
    // Adicionar empresa_id automaticamente se houver empresa selecionada
    const dadosComEmpresa = addEmpresaIdToData(dados);
    
    const response = await request(`/insurances/${id}`, {
      method: 'PUT',
      body: dadosComEmpresa,
    });
    return response.dados;
  },

  deletar: async (id) => {
    await request(`/insurances/${id}`, { method: 'DELETE' });
    return { sucesso: true };
  },
};

/**
 * API de Perfis
 */
export const profilesAPI = {
  listar: async (filtros = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(filtros).forEach(key => {
      if (filtros[key] !== undefined && filtros[key] !== null) {
        queryParams.append(key, filtros[key]);
      }
    });
    
    const queryString = queryParams.toString();
    const endpoint = `/profiles${queryString ? `?${queryString}` : ''}`;
    
    const response = await request(endpoint, { method: 'GET' });
    return response.dados || [];
  },

  buscarPorId: async (id) => {
    const response = await request(`/profiles/${id}`, { method: 'GET' });
    return response.dados;
  },

  criar: async (dados) => {
    const response = await request('/profiles', {
      method: 'POST',
      body: dados,
    });
    return response.dados;
  },

  atualizar: async (id, dados) => {
    const response = await request(`/profiles/${id}`, {
      method: 'PUT',
      body: dados,
    });
    return response.dados;
  },

  deletar: async (id) => {
    await request(`/profiles/${id}`, { method: 'DELETE' });
    return { sucesso: true };
  },

  buscarMinhasPermissoes: async () => {
    const response = await request('/profiles/permissions/me', { method: 'GET' });
    return response.dados || { modules: [], companies: [], registrations: [] };
  },
};

/**
 * API de Usuários (Colaboradores)
 */
export const usersAPI = {
  listar: async (filtros = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(filtros).forEach(key => {
      if (filtros[key] !== undefined && filtros[key] !== null) {
        queryParams.append(key, filtros[key]);
      }
    });
    
    const queryString = queryParams.toString();
    const endpoint = `/users${queryString ? `?${queryString}` : ''}`;
    
    const response = await request(endpoint, { method: 'GET' });
    return response.dados || [];
  },

  buscarPorId: async (id) => {
    const response = await request(`/users/${id}`, { method: 'GET' });
    return response.dados;
  },

  criar: async (dados) => {
    const response = await request('/users', {
      method: 'POST',
      body: dados,
    });
    return response.dados;
  },

  atualizar: async (id, dados) => {
    const response = await request(`/users/${id}`, {
      method: 'PUT',
      body: dados,
    });
    return response.dados;
  },

  reativar: async (email) => {
    const response = await request('/users/reativar', {
      method: 'POST',
      body: { email },
    });
    return response.dados;
  },

  deletar: async (id) => {
    const response = await request(`/users/${id}`, { method: 'DELETE' });
    return response.dados;
  },
};

/**
 * API de Contas a Pagar
 */
export const accountsPayableAPI = {
  listar: async (filtros = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(filtros).forEach(key => {
      if (filtros[key] !== undefined && filtros[key] !== null) {
        queryParams.append(key, filtros[key]);
      }
    });
    
    const queryString = queryParams.toString();
    const endpoint = `/contas-pagar${queryString ? `?${queryString}` : ''}`;
    
    const response = await request(endpoint, { method: 'GET' });
    return response.dados || [];
  },

  buscarPorId: async (id) => {
    const response = await request(`/contas-pagar/${id}`, { method: 'GET' });
    return response.dados;
  },

  criar: async (dados) => {
    const response = await request('/contas-pagar', {
      method: 'POST',
      body: dados,
    });
    return response.dados;
  },

  atualizar: async (id, dados) => {
    const response = await request(`/contas-pagar/${id}`, {
      method: 'PUT',
      body: dados,
    });
    return response.dados;
  },

  deletar: async (id) => {
    await request(`/contas-pagar/${id}`, { method: 'DELETE' });
    return { sucesso: true };
  },
};

/**
 * API de Contas a Receber
 */
export const accountsReceivableAPI = {
  listar: async (filtros = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(filtros).forEach(key => {
      if (filtros[key] !== undefined && filtros[key] !== null) {
        queryParams.append(key, filtros[key]);
      }
    });
    
    const queryString = queryParams.toString();
    const endpoint = `/contas-receber${queryString ? `?${queryString}` : ''}`;
    
    const response = await request(endpoint, { method: 'GET' });
    return response.dados || [];
  },

  buscarPorId: async (id) => {
    const response = await request(`/contas-receber/${id}`, { method: 'GET' });
    return response.dados;
  },

  criar: async (dados) => {
    const response = await request('/contas-receber', {
      method: 'POST',
      body: dados,
    });
    return response.dados;
  },

  atualizar: async (id, dados) => {
    const response = await request(`/contas-receber/${id}`, {
      method: 'PUT',
      body: dados,
    });
    return response.dados;
  },

  deletar: async (id) => {
    await request(`/contas-receber/${id}`, { method: 'DELETE' });
    return { sucesso: true };
  },
};

/**
 * API de Parâmetros SEFAZ
 */
export const sefazParamsAPI = {
  /**
   * Listar todos os parâmetros
   */
  listar: async () => {
    const response = await request('/sefaz-params', { method: 'GET' });
    return response || [];
  },

  /**
   * Buscar parâmetros por tipo
   */
  buscarPorTipo: async (tipo) => {
    const response = await request(`/sefaz-params/${tipo}`, { method: 'GET' });
    return response;
  },

  /**
   * Atualizar parâmetros
   */
  atualizar: async (tipo, dados) => {
    const response = await request(`/sefaz-params/${tipo}`, {
      method: 'PUT',
      body: dados,
    });
    return response;
  },

  /**
   * Incrementar número sequencial
   */
  incrementarNumero: async (tipo) => {
    const response = await request(`/sefaz-params/${tipo}/incrementar`, {
      method: 'POST',
    });
    return response;
  },
};

/**
 * API de Rastreamento
 */
export const rastreamentoAPI = {
  /**
   * Enviar coordenadas de rastreamento
   */
  enviarCoordenadas: async (dados) => {
    const response = await request('/rastreamento', {
      method: 'POST',
      body: dados,
    });
    return response.dados;
  },

  /**
   * Buscar última localização de uma carga
   */
  buscarUltimaLocalizacao: async (cargaId) => {
    try {
      const response = await request(`/rastreamento/carga/${cargaId}/ultima`, { method: 'GET' });
      // Se response.dados for null ou undefined, retornar null
      return response.dados || null;
    } catch (error) {
      // Se for erro 404 ou similar, retornar null ao invés de lançar erro
      if (error.message && (error.message.includes('404') || error.message.includes('não encontrado'))) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Listar histórico de rastreamento de uma carga
   */
  listarHistorico: async (cargaId, limite = 100) => {
    const response = await request(`/rastreamento/carga/${cargaId}/historico?limite=${limite}`, { method: 'GET' });
    return response.dados || [];
  },

  /**
   * Buscar últimas localizações de todas as cargas que têm rastreamento
   */
  buscarUltimasLocalizacoesTodasCargas: async () => {
    const response = await request('/rastreamento/ultimas-localizacoes', { method: 'GET' });
    return response.dados || [];
  },
};

/**
 * API de Cargas
 */
export const cargasAPI = {
  /**
   * Listar todas as cargas
   */
  listar: async (filtros = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(filtros).forEach(key => {
      if (filtros[key] !== undefined && filtros[key] !== null) {
        queryParams.append(key, filtros[key]);
      }
    });
    
    // Adicionar empresa_id automaticamente se houver empresa selecionada
    addEmpresaIdToQueryParams(queryParams);
    
    const queryString = queryParams.toString();
    const endpoint = `/cargas${queryString ? `?${queryString}` : ''}`;
    
    const response = await request(endpoint, { method: 'GET' });
    return response.dados || [];
  },

  /**
   * Buscar carga por ID
   */
  buscar: async (id) => {
    const response = await request(`/cargas/${id}`, { method: 'GET' });
    return response.dados;
  },

  /**
   * Buscar carga por ID (alias para buscar)
   */
  buscarPorId: async (id) => {
    const response = await request(`/cargas/${id}`, { method: 'GET' });
    return response.dados;
  },

  /**
   * Buscar carga por token (rota pública para link de acompanhamento)
   */
  buscarPorToken: async (token) => {
    const response = await request(`/cargas/token/${token}`, { method: 'GET' });
    return response.dados;
  },

  /**
   * Criar nova carga
   */
  criar: async (dadosCarga) => {
    // Adicionar empresa_id automaticamente se houver empresa selecionada
    const dadosComEmpresa = addEmpresaIdToData(dadosCarga);
    
    const response = await request('/cargas', {
      method: 'POST',
      body: dadosComEmpresa,
    });
    return response.dados;
  },

  /**
   * Atualizar carga
   */
  atualizar: async (id, dadosCarga) => {
    // Adicionar empresa_id automaticamente se houver empresa selecionada
    const dadosComEmpresa = addEmpresaIdToData(dadosCarga);
    
    const response = await request(`/cargas/${id}`, {
      method: 'PUT',
      body: dadosComEmpresa,
    });
    return response.dados;
  },

  /**
   * Deletar carga
   */
  deletar: async (id) => {
    await request(`/cargas/${id}`, { method: 'DELETE' });
    return { sucesso: true };
  },

  /**
   * Relacionar viagens a uma carga
   */
  relacionarViagens: async (cargaId, viagensIds) => {
    const response = await request(`/cargas/${cargaId}/viagens`, {
      method: 'POST',
      body: { viagens_ids: viagensIds },
    });
    return response.dados;
  },

  /**
   * Gerar ou obter link de acompanhamento
   * Retorna o link existente se já foi gerado, ou gera um novo
   */
  gerarLinkAcompanhamento: async (cargaId) => {
    const response = await request(`/cargas/${cargaId}/gerar-link`, {
      method: 'POST',
    });
    return response.dados;
  },

  /**
   * Iniciar viagem (atualizar data_inicio_viagem)
   * Rota pública (sem autenticação)
   */
  iniciarViagem: async (cargaId) => {
    const response = await request(`/cargas/${cargaId}/iniciar-viagem`, {
      method: 'POST',
    });
    return response.dados;
  },

  /**
   * Encerrar viagem (atualizar data_fim_viagem e status)
   * Rota pública (sem autenticação)
   */
  encerrarViagem: async (cargaId) => {
    const response = await request(`/cargas/${cargaId}/encerrar-viagem`, {
      method: 'POST',
    });
    return response.dados;
  },
};

/**
 * API de Naturezas
 */
export const naturezasAPI = {
  listar: async (filtros = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(filtros).forEach(key => {
      if (filtros[key] !== undefined && filtros[key] !== null) {
        queryParams.append(key, filtros[key]);
      }
    });
    
    const queryString = queryParams.toString();
    const endpoint = `/naturezas${queryString ? `?${queryString}` : ''}`;
    
    const response = await request(endpoint, { method: 'GET' });
    console.log('📥 [naturezasAPI.listar] Resposta da API:', {
      response,
      dados: response.dados,
      dadosLength: response.dados?.length || 0,
      sucesso: response.sucesso,
    });
    
    const naturezas = response.dados || [];
    console.log('📤 [naturezasAPI.listar] Retornando naturezas:', {
      count: naturezas.length,
      naturezas: naturezas,
    });
    
    return naturezas;
  },

  buscarPorId: async (id) => {
    const response = await request(`/naturezas/${id}`, { method: 'GET' });
    return response.dados;
  },

  criar: async (dados) => {
    const response = await request('/naturezas', {
      method: 'POST',
      body: dados,
    });
    return response.dados;
  },

  atualizar: async (id, dados) => {
    const response = await request(`/naturezas/${id}`, {
      method: 'PUT',
      body: dados,
    });
    return response.dados;
  },

  deletar: async (id) => {
    await request(`/naturezas/${id}`, { method: 'DELETE' });
    return { sucesso: true };
  },
};

/**
 * API de Clientes
 */
export const clientesAPI = {
  listar: async (filtros = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(filtros).forEach(key => {
      if (filtros[key] !== undefined && filtros[key] !== null) {
        queryParams.append(key, filtros[key]);
      }
    });
    
    const queryString = queryParams.toString();
    const endpoint = `/clientes${queryString ? `?${queryString}` : ''}`;
    
    const response = await request(endpoint, { method: 'GET' });
    return response.dados || [];
  },

  buscarPorId: async (id) => {
    const response = await request(`/clientes/${id}`, { method: 'GET' });
    return response.dados;
  },

  criar: async (dados) => {
    // Adicionar empresa_id automaticamente se houver empresa selecionada
    const dadosComEmpresa = addEmpresaIdToData(dados);
    
    const response = await request('/clientes', {
      method: 'POST',
      body: dadosComEmpresa,
    });
    return response.dados;
  },

  atualizar: async (id, dados) => {
    // Adicionar empresa_id automaticamente se houver empresa selecionada
    const dadosComEmpresa = addEmpresaIdToData(dados);
    
    const response = await request(`/clientes/${id}`, {
      method: 'PUT',
      body: dadosComEmpresa,
    });
    return response.dados;
  },

  deletar: async (id) => {
    await request(`/clientes/${id}`, { method: 'DELETE' });
    return { sucesso: true };
  },
};

/**
 * API de Fornecedores
 */
export const fornecedoresAPI = {
  listar: async (filtros = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(filtros).forEach(key => {
      if (filtros[key] !== undefined && filtros[key] !== null) {
        queryParams.append(key, filtros[key]);
      }
    });
    
    const queryString = queryParams.toString();
    const endpoint = `/fornecedores${queryString ? `?${queryString}` : ''}`;
    
    const response = await request(endpoint, { method: 'GET' });
    return response.dados || [];
  },

  buscarPorId: async (id) => {
    const response = await request(`/fornecedores/${id}`, { method: 'GET' });
    return response.dados;
  },

  criar: async (dados) => {
    // Adicionar empresa_id automaticamente se houver empresa selecionada
    const dadosComEmpresa = addEmpresaIdToData(dados);
    
    const response = await request('/fornecedores', {
      method: 'POST',
      body: dadosComEmpresa,
    });
    return response.dados;
  },

  atualizar: async (id, dados) => {
    // Adicionar empresa_id automaticamente se houver empresa selecionada
    const dadosComEmpresa = addEmpresaIdToData(dados);
    
    const response = await request(`/fornecedores/${id}`, {
      method: 'PUT',
      body: dadosComEmpresa,
    });
    return response.dados;
  },

  deletar: async (id) => {
    await request(`/fornecedores/${id}`, { method: 'DELETE' });
    return { sucesso: true };
  },
};

/**
 * API de Embarcadores
 */
export const embarcadoresAPI = {
  listar: async (filtros = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(filtros).forEach((key) => {
      if (filtros[key] !== undefined && filtros[key] !== null) {
        queryParams.append(key, filtros[key]);
      }
    });
    const queryString = queryParams.toString();
    const endpoint = `/embarcadores${queryString ? `?${queryString}` : ''}`;
    const response = await request(endpoint, { method: 'GET' });
    return response.dados || [];
  },

  buscarPorId: async (id) => {
    const response = await request(`/embarcadores/${id}`, { method: 'GET' });
    return response.dados;
  },

  criar: async (dados) => {
    const dadosComEmpresa = addEmpresaIdToData(dados);
    const response = await request('/embarcadores', {
      method: 'POST',
      body: dadosComEmpresa,
    });
    return response.dados;
  },

  atualizar: async (id, dados) => {
    const dadosComEmpresa = addEmpresaIdToData(dados);
    const response = await request(`/embarcadores/${id}`, {
      method: 'PUT',
      body: dadosComEmpresa,
    });
    return response.dados;
  },

  deletar: async (id) => {
    await request(`/embarcadores/${id}`, { method: 'DELETE' });
    return { sucesso: true };
  },
};

export default {
  auth: authAPI,
  viagens: viagensAPI,
  fretes: fretesAPI,
  cargas: cargasAPI,
  cte: cteAPI,
  mdfe: mdfeAPI,
  providers: providersAPI,
  truckers: truckersAPI,
  insurances: insurancesAPI,
  accountsPayable: accountsPayableAPI,
  accountsReceivable: accountsReceivableAPI,
  clientes: clientesAPI,
  naturezas: naturezasAPI,
  fornecedores: fornecedoresAPI,
  embarcadores: embarcadoresAPI,
  sefazParams: sefazParamsAPI,
  rastreamento: rastreamentoAPI,
};


